// ============================================================
// VehiclePathOverlay.jsx
//
// 2D "simulate approach" overlay for CadCanvas. Rendered as a SIBLING
// <svg>, absolutely positioned on top of CadCanvas's own imperatively-
// drawn <svg> — deliberately NOT wired into CadCanvas's drawAll()
// function. That function is a large, delicate piece of imperative DOM
// code (see the "Critical CadCanvas pattern" note in project memory
// about its useCallback deps array), so this overlay owns its own
// React state and lifecycle entirely separately, sharing only the
// scale/pan/zoom numbers it needs to line up visually.
//
// This calls utils/vehiclePath.js — the SAME functions the 3D counterpart
// (canvas3d/VehiclePath3D.jsx) calls — so the two views can never
// disagree about the same door/lift/vehicle combination, in either auto
// or manual path mode.
//
// ---- Manual mode ----
// The user builds the path as an ordered chain of waypoints: door ->
// wp[0] -> wp[1] -> ... -> lift. Each waypoint has its own forward/reverse
// choice for the leg ARRIVING at it, so a truck squeezing past a
// neighboring lift can be laid out point-by-point exactly the way a real
// driver would thread it, rather than hoping the auto-search happens to
// find that line.
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { VEHICLE_CATALOG, getVehicle } from '../data/vehicleCatalog';
import { getDoorEndpoints } from '../utils/shopGeometry';
import {
  planApproach, nearestOverheadDoor, doorEntryPose, liftTargetPoses,
  buildApproachContext, defaultManualWaypoint, solveManualPath,
} from '../utils/vehiclePath';

const PANEL_BG = 'rgba(2,12,28,0.9)';
const BORDER = '#3399ff88';
const GEAR_COLORS = { forward: '#3ecf6e', reverse: '#ffcc00' };
const ACTIVE_DOOR_COLOR = '#00e5a8';

// CadCanvas's drag handlers call setPlaced() on every single mousemove
// event while dragging ANY equipment (not just the lift being simulated
// onto) — see onMouseMove around line 1544. That means `placed` gets a
// brand-new array reference dozens of times per second during a drag.
// The auto-solver (planApproach) is NOT cheap — up to ~2s worst case with
// the 3-correction stage — so without this debounce, having the sim
// panel open while repositioning ANY item on the canvas re-runs the full
// search on every mouse-move tick instead of once when the drag settles,
// which is exactly the "very resource intensive" behavior this fixes.
// Manual mode's solveManualPath is cheap either way, but shares this so
// its dependent obstacle context doesn't churn either.
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function VehiclePathOverlay({
  shop, doors, placed, liftItem, vehicleKey, onVehicleKeyChange,
  doorId, onDoorIdChange,
  pathMode, onPathModeChange,
  waypoints, onWaypointsChange,
  goalGear, onGoalGearChange,
  goalFlip, onGoalFlipChange,
  speed, onSpeedChange,
  scale, ox, oy, size,
}) {
  const [playing, setPlaying] = useState(false);
  const [carPose, setCarPose] = useState(null);
  const rafRef = useRef(null);
  const dragRef = useRef(null); // { index, which: 'pos'|'heading' } | null

  // Debounced so dragging OTHER equipment around the canvas while this
  // panel is open doesn't re-run the (potentially ~2s) auto-solver on
  // every mouse-move tick — see useDebouncedValue above.
  const debouncedPlaced = useDebouncedValue(placed, 200);
  const debouncedDoors = useDebouncedValue(doors, 200);
  const debouncedLiftItem = useDebouncedValue(liftItem, 200);

  const overheadDoors = useMemo(() => debouncedDoors.filter(d => d.type === 'overhead'), [debouncedDoors]);

  // Manual door pick wins if set (and still exists); otherwise nearest,
  // same fallback rule used everywhere else in this feature.
  const door = useMemo(() => {
    if (doorId) {
      const picked = overheadDoors.find(d => d.id === doorId);
      if (picked) return picked;
    }
    return nearestOverheadDoor(debouncedLiftItem, debouncedDoors, shop);
  }, [doorId, overheadDoors, debouncedLiftItem, debouncedDoors, shop]);

  const startPose = useMemo(() => (door ? doorEntryPose(door, shop) : null), [door, shop]);
  // Geometry of the active door's actual opening (its two wall-endpoints),
  // used to draw a highlight over it — shown as soon as a door is picked
  // (auto or manual), independent of whether a path has been solved or
  // played yet, so you can see which opening will be used before hitting
  // Play.
  const activeDoorEnds = useMemo(() => (door ? getDoorEndpoints(door, shop) : null), [door, shop]);
  const ctx = useMemo(() => {
    const built = buildApproachContext({ shop, doors: debouncedDoors, liftItem: debouncedLiftItem, vehicleKey, placed: debouncedPlaced });
    return { ...built, doorPoint: startPose ? { x: startPose.x, y: startPose.y } : null };
  }, [shop, debouncedDoors, debouncedLiftItem, vehicleKey, debouncedPlaced, startPose]);
  const goalPoses = useMemo(() => liftTargetPoses(debouncedLiftItem), [debouncedLiftItem]);
  const goalPose = goalPoses[goalFlip ? 1 : 0];

  const autoResult = useMemo(() => {
    if (!door) return { valid: false, reason: 'space', message: 'no overhead door found on this shop' };
    return planApproach({ shop, door, doors: debouncedDoors, liftItem: debouncedLiftItem, vehicleKey, placed: debouncedPlaced });
  }, [shop, door, debouncedDoors, debouncedLiftItem, vehicleKey, debouncedPlaced]);

  const manualResult = useMemo(() => {
    if (pathMode !== 'manual' || !startPose) return { valid: false };
    return solveManualPath(startPose, waypoints, goalPose, goalGear, ctx.radius, ctx);
  }, [pathMode, startPose, waypoints, goalPose, goalGear, ctx]);

  const result = pathMode === 'manual' ? manualResult : autoResult;

  // Shop-feet -> screen-pixel, matching CadCanvas's own scale/ox/oy exactly.
  const toScreen = (p) => ({ x: p.x * scale + ox, y: p.y * scale + oy });
  const toShop = (sx, sy) => ({ x: (sx - ox) / scale, y: (sy - oy) / scale });

  const pathD = useMemo(() => {
    if (!result.points || result.points.length === 0) return '';
    return result.points.map((p, i) => {
      const s = toScreen(p);
      return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`;
    }).join(' ');
  }, [result, scale, ox, oy]);

  const play = () => {
    if (!result.points || result.points.length === 0) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPlaying(true);
    const points = result.points;
    const baseDurationMs = Math.max(1200, points.length * 12);
    const durationMs = baseDurationMs / (speed || 1);
    const start = performance.now();
    const step = (ts) => {
      const t = Math.min(1, (ts - start) / durationMs);
      const idx = Math.min(points.length - 1, Math.floor(t * (points.length - 1)));
      setCarPose(points[idx]);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setPlaying(false);
      }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  // ---- Manual waypoint dragging ----
  // Each waypoint gets two handles: a big circle for position, a small
  // circle offset in the heading direction for rotation. Plain
  // window-level pointer listeners rather than an SVG drag library —
  // simplest thing that works for an arbitrary number of draggable
  // points. `waypointsRef` holds the latest array so the move handler
  // doesn't need `waypoints` in its deps (which would otherwise force a
  // full listener add/remove on every single drag frame).
  const waypointsRef = useRef(waypoints);
  useEffect(() => { waypointsRef.current = waypoints; }, [waypoints]);

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const svgEl = document.getElementById('vehicle-path-overlay-svg');
      if (!svgEl) return;
      const bounds = svgEl.getBoundingClientRect();
      const sx = e.clientX - bounds.left, sy = e.clientY - bounds.top;
      const list = waypointsRef.current;
      const current = list[drag.index];
      if (!current) return;
      let updated;
      if (drag.which === 'pos') {
        const shopPt = toShop(sx, sy);
        updated = { ...current, x: shopPt.x, y: shopPt.y };
      } else {
        const center = toScreen(current);
        updated = { ...current, heading: Math.atan2(sy - center.y, sx - center.x) };
      }
      onWaypointsChange(list.map((w, i) => (i === drag.index ? updated : w)));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line
  }, [scale, ox, oy, onWaypointsChange]);

  const startDrag = (index, which) => (e) => {
    e.stopPropagation();
    dragRef.current = { index, which };
  };

  const addWaypoint = () => {
    const fromPose = waypoints.length > 0 ? waypoints[waypoints.length - 1] : startPose;
    if (!fromPose) return;
    const prevGear = waypoints.length > 0 ? waypoints[waypoints.length - 1].gear : 'forward';
    const gear = prevGear === 'forward' ? 'reverse' : 'forward';
    const wp = { ...defaultManualWaypoint(fromPose, goalPose, gear), id: `wp-${Date.now()}-${Math.random()}` };
    onWaypointsChange([...waypoints, wp]);
  };

  const removeWaypoint = (index) => {
    onWaypointsChange(waypoints.filter((_, i) => i !== index));
  };

  const toggleWaypointGear = (index) => {
    onWaypointsChange(waypoints.map((w, i) => (i === index ? { ...w, gear: w.gear === 'forward' ? 'reverse' : 'forward' } : w)));
  };

  if (!size) return null;

  const vehicle = getVehicle(vehicleKey);
  const carLenPx = vehicle.length * scale;
  const carWidPx = vehicle.width * scale;

  return (
    <>
      <svg
        id="vehicle-path-overlay-svg"
        width={size.width}
        height={size.height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {/* Active door highlight — shown as soon as a door is picked (auto
            or manual), independent of whether a path has been solved or
            played, so it's obvious which opening the simulation will use
            before you hit Play. Pulses gently so it reads at a glance
            without being drawn ON TOP of the path once one exists. */}
        {activeDoorEnds && (() => {
          const s1 = toScreen(activeDoorEnds.start);
          const s2 = toScreen(activeDoorEnds.end);
          const mid = toScreen(activeDoorEnds.midpoint);
          // Small outward-pointing chevron so it's clear which SIDE of the
          // wall the vehicle enters from, not just which span is active.
          const outAngle = Math.atan2(activeDoorEnds.outwardVec.y, activeDoorEnds.outwardVec.x);
          const chevX = mid.x + 16 * Math.cos(outAngle);
          const chevY = mid.y + 16 * Math.sin(outAngle);
          return (
            <g>
              <line
                x1={s1.x} y1={s1.y} x2={s2.x} y2={s2.y}
                stroke={ACTIVE_DOOR_COLOR} strokeWidth={6} strokeLinecap="round"
                opacity={0.85}
              >
                <animate attributeName="opacity" values="0.5;0.95;0.5" dur="1.6s" repeatCount="indefinite" />
              </line>
              <g transform={`translate(${chevX},${chevY}) rotate(${(outAngle * 180) / Math.PI})`}>
                <path d="M -6,-5 L 4,0 L -6,5" fill="none" stroke={ACTIVE_DOOR_COLOR} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </g>
          );
        })()}

        {result.points && result.points.length > 0 && (
          <>
            <path d={pathD} fill="none" stroke={result.valid ? '#7f77dd' : '#e2534a'} strokeWidth={2.5} strokeDasharray="6 5" />
            {carPose && (() => {
              const s = toScreen(carPose);
              const angleDeg = (carPose.heading * 180) / Math.PI + 90;
              // Real physical footprint, in screen pixels, so the icon
              // reads true-to-scale at any zoom level instead of a fixed
              // 14x22px blob that only looked right at one zoom.
              const halfW = carWidPx / 2, halfL = carLenPx / 2;
              return (
                <g transform={`translate(${s.x},${s.y}) rotate(${angleDeg})`}>
                  <rect x={-halfW} y={-halfL} width={carWidPx} height={carLenPx} rx={Math.min(4, halfW * 0.4)} fill="#d85a30" />
                  <line x1={-halfW * 0.7} y1={-halfL + halfW * 0.5} x2={halfW * 0.7} y2={-halfL + halfW * 0.5} stroke="#4a1b0c" strokeWidth={1.5} />
                </g>
              );
            })()}
          </>
        )}

        {/* Manual mode: one draggable position+heading handle PER waypoint,
            color-coded by that leg's gear (green = forward, yellow =
            reverse). pointerEvents re-enabled just on the handles,
            everything else on this overlay stays click-through. */}
        {pathMode === 'manual' && waypoints.map((wp, i) => {
          const c = toScreen(wp);
          const headingHandleDist = 26;
          const hx = c.x + headingHandleDist * Math.cos(wp.heading);
          const hy = c.y + headingHandleDist * Math.sin(wp.heading);
          const color = GEAR_COLORS[wp.gear] || GEAR_COLORS.forward;
          return (
            <g key={wp.id || i}>
              <line x1={c.x} y1={c.y} x2={hx} y2={hy} stroke={color} strokeWidth={2} />
              <circle
                cx={c.x} cy={c.y} r={10}
                fill={color} stroke="#03101f" strokeWidth={2}
                style={{ pointerEvents: 'all', cursor: 'grab' }}
                onPointerDown={startDrag(i, 'pos')}
              />
              <text x={c.x} y={c.y + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#03101f" style={{ pointerEvents: 'none' }}>
                {i + 1}
              </text>
              <circle
                cx={hx} cy={hy} r={6}
                fill="#fff3c4" stroke="#7a5c00" strokeWidth={1.5}
                style={{ pointerEvents: 'all', cursor: 'grab' }}
                onPointerDown={startDrag(i, 'heading')}
              />
            </g>
          );
        })}
      </svg>

      {/* Control panel — floating card, matches the app's Bay Zone button styling */}
      <div style={{
        position: 'absolute', top: 52, left: 12, zIndex: 10, width: 260,
        background: PANEL_BG, border: `1px solid ${BORDER}`, borderRadius: 7,
        padding: 10, color: '#c8dff0', fontSize: 11, fontFamily: 'inherit',
        maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, color: '#3399ff', marginBottom: 6 }}>Simulate vehicle approach</div>
        <select
          value={vehicleKey}
          onChange={(e) => onVehicleKeyChange(e.target.value)}
          style={{ width: '100%', marginBottom: 8, fontSize: 11, background: '#0a1628', color: '#c8dff0', border: '1px solid #3399ff44', borderRadius: 4, padding: '4px 6px' }}
        >
          {Object.entries(VEHICLE_CATALOG).map(([key, v]) => (
            <option key={key} value={key}>{v.label}</option>
          ))}
        </select>

        {overheadDoors.length > 0 && (
          <select
            value={doorId || ''}
            onChange={(e) => onDoorIdChange(e.target.value ? Number(e.target.value) : null)}
            style={{ width: '100%', marginBottom: 8, fontSize: 11, background: '#0a1628', color: '#c8dff0', border: '1px solid #3399ff44', borderRadius: 4, padding: '4px 6px' }}
          >
            <option value="">Nearest door (auto)</option>
            {overheadDoors.map((d, i) => (
              <option key={d.id} value={d.id}>Overhead door {i + 1}</option>
            ))}
          </select>
        )}

        {/* Auto/Manual toggle */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {['auto', 'manual'].map(mode => (
            <button
              key={mode}
              onClick={() => onPathModeChange(mode)}
              style={{
                flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 0', cursor: 'pointer',
                borderRadius: 5, border: `1px solid ${BORDER}`,
                background: pathMode === mode ? '#3399ff' : 'transparent',
                color: pathMode === mode ? '#03101f' : '#3399ff',
              }}
            >
              {mode === 'auto' ? 'Auto' : 'Manual'}
            </button>
          ))}
        </div>

        {pathMode === 'manual' && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: '#9fc2e0', marginBottom: 6 }}>
              Door → {waypoints.length === 0 ? '(no points yet)' : `${waypoints.length} point${waypoints.length === 1 ? '' : 's'}`} → Lift.
              Drag a dot to reposition, the small handle to set its heading.
            </div>

            {/* Per-waypoint list: gear toggle + remove, in path order */}
            {waypoints.map((wp, i) => (
              <div key={wp.id || i} style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                background: '#0a1628', border: '1px solid #3399ff33', borderRadius: 4, padding: '4px 6px',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%', background: GEAR_COLORS[wp.gear],
                  color: '#03101f', fontWeight: 700, fontSize: 10, flexShrink: 0,
                }}>{i + 1}</span>
                <span style={{ flex: 1 }}>Point {i + 1}</span>
                <button
                  onClick={() => toggleWaypointGear(i)}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 6px', cursor: 'pointer',
                    borderRadius: 4, border: '1px solid #3399ff44',
                    background: 'transparent', color: GEAR_COLORS[wp.gear],
                  }}
                  title="Toggle forward/reverse for the leg arriving at this point"
                >
                  {wp.gear === 'forward' ? 'FWD' : 'REV'}
                </button>
                <button
                  onClick={() => removeWaypoint(i)}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 6px', cursor: 'pointer',
                    borderRadius: 4, border: '1px solid #e2534a66', background: 'transparent', color: '#e2534a',
                  }}
                  title="Remove this point"
                >
                  ×
                </button>
              </div>
            ))}

            <button
              onClick={addWaypoint}
              style={{
                width: '100%', marginBottom: 6, background: 'transparent',
                border: '1px dashed #3399ff88', borderRadius: 4, color: '#3399ff',
                fontSize: 11, fontWeight: 700, padding: '5px 0', cursor: 'pointer',
              }}
            >
              + Add point
            </button>

            {/* Final leg into the lift — not a draggable point (its
                position is fixed by the lift itself), just its gear. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
              background: '#0a1628', border: '1px solid #3399ff33', borderRadius: 4, padding: '4px 6px',
            }}>
              <span style={{ flex: 1 }}>Final leg → lift</span>
              <button
                onClick={() => onGoalGearChange(goalGear === 'forward' ? 'reverse' : 'forward')}
                style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px', cursor: 'pointer',
                  borderRadius: 4, border: '1px solid #3399ff44',
                  background: 'transparent', color: GEAR_COLORS[goalGear],
                }}
              >
                {goalGear === 'forward' ? 'FWD' : 'REV'}
              </button>
            </div>

            <button
              onClick={() => onGoalFlipChange(!goalFlip)}
              style={{
                display: 'block', width: '100%', background: 'transparent',
                border: '1px solid #3399ff44', borderRadius: 4, color: '#9fc2e0',
                fontSize: 10, padding: '4px 0', cursor: 'pointer',
              }}
            >
              Flip lift end ({goalFlip ? 'B' : 'A'})
            </button>
          </div>
        )}

        {!door && <div style={{ color: '#f0997b' }}>No overhead door found on this shop.</div>}

        {door && result.valid && (
          <div style={{ color: '#ff9900', fontWeight: 700, marginBottom: 6, marginTop: 6 }}>
            {result.corrections} correction{result.corrections === 1 ? '' : 's'} needed
          </div>
        )}
        {door && !result.valid && pathMode === 'auto' && (
          <div style={{ color: '#f0997b', marginBottom: 6 }}>
            Blocked — {result.message || result.reason}
          </div>
        )}
        {door && !result.valid && pathMode === 'manual' && result.points && (
          <div style={{ color: '#f0997b', marginBottom: 6 }}>
            Warning — {result.message || 'clips something'} (playback still allowed)
          </div>
        )}
        {door && !result.valid && pathMode === 'manual' && !result.points && (
          <div style={{ color: '#f0997b', marginBottom: 6 }}>
            {result.message || 'add a point to build a path'}
          </div>
        )}

        {/* Playback speed — shared with the 3D view via App-level state, so
            switching views mid-scrub keeps the same rate. */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span>Speed</span><span>{(speed || 1).toFixed(1)}x</span>
          </div>
          <input
            type="range" min={0.25} max={3} step={0.25}
            value={speed || 1}
            onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>

        <button
          onClick={play}
          disabled={!result.points || result.points.length === 0 || playing}
          style={{
            width: '100%', background: 'transparent', border: '1px solid #3399ff88',
            borderRadius: 6, color: '#3399ff', fontSize: 11, fontWeight: 700,
            padding: '6px 10px', cursor: (result.points && result.points.length) ? 'pointer' : 'not-allowed',
            opacity: (result.points && result.points.length) ? 1 : 0.5,
          }}
        >
          {playing ? 'Playing…' : '▶ Play approach'}
        </button>
      </div>
    </>
  );
}
